import type { PolicyFramework } from '@repo/api-codegen';
import {
	useApiGetPolicyMetrics,
	useApiGetPolicyTimeseries,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { keepPreviousData } from '@tanstack/react-query';
import { memo, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser } from '../../api';
import { AnalyticsBanner } from './components/AnalyticsBanner';
import { MetricsPolicyCards } from './components/cards/MetricsPolicyCards';
import { PoliciesOverviewChart } from './components/charts/PoliciesOverviewChart';
import { MetricsOverviewTab } from './components/MetricsOverviewTab';
import { MetricsOverviewTabSkeleton } from './components/MetricsOverviewTabSkeleton';
import { policyFilterOptions } from './constants';
import { useOverviewMetricDatetime } from './hooks/useMetricOverviewDatetime';
import type { FilterOption, PoliciesOverviewTabControllerProps } from './types';

import { Box } from '@mantine/core';
import { isValidTimeRange, isValidViewOption } from './types.ts';
export const PoliciesOverviewTabController = memo(
	({ onMetricCardClick }: PoliciesOverviewTabControllerProps) => {
		const { user, workspace } = useAuthUser();
		const [policyFilterType, setPolicyFilterType] = useState('all');
		const [frameworkFilterType, setFrameworkFilterType] = useState('all');
		const {
			timeRangeValue,
			setTimeRangeValue,
			dateRangeOption,
			setDateRangeOption,
			dateFilter,
			setDateFilter,
		} = useOverviewMetricDatetime({ cacheKey: 'policies' });
		const navigate = useNavigate();

		// Create wrapper functions to handle type conversion with validation
		const handleSetDateFilter = useCallback(
			(value: string) => {
				if (isValidTimeRange(value)) {
					setDateFilter(value);
				}
			},
			[setDateFilter]
		);

		const handleSetDateRangeOption = useCallback(
			(value: string) => {
				if (isValidViewOption(value)) {
					setDateRangeOption(value);
				}
			},
			[setDateRangeOption]
		);

		const { data: metrics, isPending: isMetricsLoading } =
			useApiGetPolicyMetrics(
				{
					queryParams: {
						filter_type: policyFilterType as 'all' | 'owned' | 'subscribed',
						...(frameworkFilterType !== 'all' && {
							framework: frameworkFilterType as PolicyFramework,
						}),
						view_option: dateRangeOption,
					},
				},
				{ placeholderData: keepPreviousData }
			);

		const { data: timeseries, isPending: isTimeseriesLoading } =
			useApiGetPolicyTimeseries(
				{
					queryParams: {
						filter_type: policyFilterType as 'all' | 'owned' | 'subscribed',
						...(frameworkFilterType !== 'all' && {
							framework: frameworkFilterType as PolicyFramework,
						}),
						time_range: dateFilter,
						view_option: dateRangeOption,
					},
				},
				{ placeholderData: keepPreviousData }
			);

		const availableFrameworks = useMemo(() => {
			if (!metrics?.framework_breakdown) return [];
			return Object.keys(metrics.framework_breakdown) as PolicyFramework[];
		}, [metrics?.framework_breakdown]);

		const frameworkFilterOptions = useMemo<FilterOption[]>(
			() => [
				{ label: 'All frameworks', value: 'all' },
				...availableFrameworks.map((framework) => ({
					label: framework,
					value: framework,
				})),
			],
			[availableFrameworks]
		);

		const handleFilterTypesChange = useCallback(
			(newFilterTypes: string[]) => {
				const newPolicyFilterType = newFilterTypes[0];
				const newFrameworkFilterType = newFilterTypes[1];
				setPolicyFilterType(newPolicyFilterType);
				setFrameworkFilterType(newFrameworkFilterType);

				// Track policy filter change
				const policyFilterLabel = policyFilterOptions.find(
					(opt) => opt.value === newPolicyFilterType
				)?.label;
				if (policyFilterLabel) {
					trackEvent(
						'policies/overview/filter_change',
						{
							filter_type: 'policy',
							filter_value: newPolicyFilterType,
							filter_label: policyFilterLabel,
						},
						user,
						workspace!
					);
				}

				// Track framework filter change
				const frameworkFilterLabel = frameworkFilterOptions.find(
					(opt) => opt.value === newFrameworkFilterType
				)?.label;
				if (frameworkFilterLabel) {
					trackEvent(
						'policies/overview/filter_change',
						{
							filter_type: 'framework',
							filter_value: newFrameworkFilterType,
							filter_label: frameworkFilterLabel,
						},
						user,
						workspace!
					);
				}
			},
			[user, workspace, frameworkFilterOptions]
		);

		const handleNavigate = useCallback(() => {
			navigate(`/analytics`);
		}, [navigate]);

		if (isMetricsLoading || isTimeseriesLoading || !timeseries || !metrics) {
			return <MetricsOverviewTabSkeleton cards={2} />;
		}

		return (
			<MetricsOverviewTab
				filterOptions={[policyFilterOptions, frameworkFilterOptions]}
				filterTypes={[policyFilterType, frameworkFilterType]}
				setFilterTypes={handleFilterTypesChange}
			>
				<MetricsPolicyCards
					policyMetrics={metrics}
					onMetricCardClick={onMetricCardClick}
				/>
				<AnalyticsBanner handleNavigate={handleNavigate} />
				<Box pb="xl">
					<PoliciesOverviewChart
						timeseries={timeseries}
						dateFilter={dateFilter || '24h'}
						setDateFilter={handleSetDateFilter}
						setTimeRangeValue={setTimeRangeValue}
						timeRangeValue={timeRangeValue}
						dateRangeOption={dateRangeOption || 'day'}
						setDateRangeOption={handleSetDateRangeOption}
					/>
				</Box>
			</MetricsOverviewTab>
		);
	}
);
